import type { FileUIPart, ModelMessage } from 'ai'
import { Plus, X } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { PromptInputProvider } from '@/components/ai-elements/prompt-input'
import { Button, buttonVariants } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  clearCsvAttachments,
  getCsvAttachmentCount,
  parseCsvText,
  setCsvAttachments,
} from '@/lib/agents/csv-store'
import { clearGkCache, prefetchGk } from '@/lib/ak'
import { getRelevantMemories, type Memory } from '@/lib/api-handlers/memories'
import { createChat, saveUserMessage, type Turn } from '@/lib/chats'
import { DEFAULT_MODEL } from '@/lib/constants/chat-constants'
import { useChatManagement } from '@/lib/hooks/use-chat-management'
import { useConnectors } from '@/lib/hooks/use-connectors'
import { useMessageStreaming } from '@/lib/hooks/use-message-streaming'
import { useSheetSuggestions } from '@/lib/hooks/use-sheet-suggestions'
import { useSnapshotCapture } from '@/lib/hooks/use-snapshot-capture'
import { useSnapshotsEnabled } from '@/lib/hooks/use-snapshots-enabled'
import { usePlatform } from '@/lib/platform'
import { uploadFileToStorage, urlToFile, useSupabaseAuth } from '@/lib/supabase'
import { cn } from '@/lib/utils'
import {
  buildUserMessageParts,
  createUserMessage,
  generateChatTitle,
  generateDisplayText,
} from '@/lib/utils/message-helpers'
import { useWorkbookKey } from '@/lib/workbook-key'
import ChatInput from './ChatInput'
import ChatTabContent from './ChatTabContent'
import { ConnectorsPage } from './ConnectorsPage'
import { CreditsHistoryPage } from './CreditsHistoryPage'
import History from './History'
import { MemoriesPage } from './MemoriesPage'
import Settings from './Settings'

export default function ChatPanel() {
  // Read pre-filled prompt from sessionStorage synchronously before first render
  const [initialPrompt, setInitialPrompt] = useState(() => {
    const prefillPrompt = sessionStorage.getItem('onboardingPrefillPrompt')
    if (prefillPrompt) {
      // Clear sessionStorage after reading
      sessionStorage.removeItem('onboardingPrefillPrompt')
      return prefillPrompt
    }
    return ''
  })

  // Connectors page state
  const [showConnectorsPage, setShowConnectorsPage] = useState(false)
  // Credits history page state
  const [showCreditsHistoryPage, setShowCreditsHistoryPage] = useState(false)
  // Memories page state
  const [showMemoriesPage, setShowMemoriesPage] = useState(false)

  const { isAuthenticated, loading: authLoading } = useSupabaseAuth()
  const {
    captureSnapshotForTurn,
    associateSnapshot,
    associateAfterSnapshot,
    startDeltaCaptureForTurn,
    finalizeTurnDeltas,
    discardTurnDeltas,
  } = useSnapshotCapture()
  const { snapshotsEnabled } = useSnapshotsEnabled()
  const platform = usePlatform()
  const workbookKey = useWorkbookKey(platform)
  const { connectors } = useConnectors()

  // Map connectors to the format expected by the agent
  const connectorInfos = useMemo(
    () =>
      connectors.map((c) => ({
        id: c.id,
        type: c.type,
        name: c.name,
        config: c.config,
      })),
    [connectors]
  )

  // Connector types list for sheet suggestions
  const connectorTypes = useMemo(
    () => [...new Set(connectors.map((c) => c.type))],
    [connectors]
  )

  // Fetch relevant memories for system prompt injection
  const [memories, setMemories] = useState<Memory[]>([])
  useEffect(() => {
    if (!isAuthenticated) {
      setMemories([])
      return
    }
    getRelevantMemories(workbookKey, 15)
      .then(setMemories)
      .catch((err) => console.warn('[ChatPanel] Failed to fetch memories', err))
  }, [isAuthenticated, workbookKey])

  // Pre-fetch the API key so it's ready when the user sends their first message
  useEffect(() => {
    if (!isAuthenticated) {
      clearGkCache()
      return
    }
    prefetchGk(DEFAULT_MODEL)
  }, [isAuthenticated])

  // Use custom hooks for state management
  const {
    chats,
    setChats,
    activeChatId,
    setActiveChatId,
    handleNewChat,
    handleDismiss,
    updateMessages,
    handleSelectChatFromHistory,
    handleMessagesLoaded,
    createNewChatIfNeeded,
    updateChatTitle,
    markChatPersisted,
    updateTurnMetadata,
  } = useChatManagement()

  const {
    loadingChatId,
    setLoadingChatId,
    sendingToChatIdRef,
    abortControllerRef,
    streamChat,
    handleStop,
  } = useMessageStreaming({
    platform,
    updateMessages,
    connectors: connectorInfos,
    memories,
    workbookKey,
  })

  const effectiveActiveChatId = activeChatId ?? chats[0]?.id ?? null
  const activeChat = effectiveActiveChatId
    ? chats.find((c) => c.id === effectiveActiveChatId)
    : null
  const isIncognito =
    effectiveActiveChatId !== null && Boolean(activeChat?.isIncognito)

  // AI-generated suggestions based on sheet content (only when empty state is showing)
  const activeChatIsEmpty = !activeChat?.messages?.length
  const { suggestions: sheetSuggestions } = useSheetSuggestions(
    platform,
    connectorTypes,
    activeChatIsEmpty
  )

  const handleToggleIncognito = useCallback(() => {
    if (!effectiveActiveChatId) {
      handleNewChat()
      return
    }
    setChats((prev) =>
      prev.map((chat) =>
        chat.id === effectiveActiveChatId
          ? { ...chat, isIncognito: !chat.isIncognito }
          : chat
      )
    )
  }, [effectiveActiveChatId, handleNewChat, setChats])

  // Handler for turn metadata loaded (converts turns to TurnMetadata)
  const handleTurnMetadataLoaded = useCallback(
    (chatId: string, turns: Turn[]) => {
      const metadata = turns.map((turn, index) => ({
        turnId: turn.id,
        messageRange: [index * 2, index * 2 + turn.messages.length] as [
          number,
          number,
        ],
        hasSnapshot: false, // Will be updated by ChatTabContent
        hasAfterSnapshot: false,
      }))
      updateTurnMetadata(chatId, metadata)
    },
    [updateTurnMetadata]
  )

  // Guard: track the workbook key that was successfully loaded so we don't
  // re-run the effect and overwrite in-flight messages.
  const loadedWorkbookKeyRef = useRef<string | null>(null)

  // Load chats on authentication
  useEffect(() => {
    if (authLoading) {
      return
    }
    if (!isAuthenticated) {
      setChats([])
      setActiveChatId(null)
      loadedWorkbookKeyRef.current = null
      return
    }

    async function loadChats() {
      try {
        if (!workbookKey) {
          // Fallback: keep legacy behavior if we can't resolve workbook identity yet
          handleNewChat()
          return
        }

        // Already loaded for this workbook — don't overwrite existing state
        if (loadedWorkbookKeyRef.current === workbookKey) {
          return
        }

        const result = await createChat(workbookKey)
        if (!result.success || !result.chat?.id) {
          handleNewChat()
          return
        }

        loadedWorkbookKeyRef.current = workbookKey

        const backendChat = {
          id: result.chat.id,
          title: result.chat.title || 'New Chat',
          messages: [] as ModelMessage[],
          createdAt: result.chat.created_at
            ? new Date(result.chat.created_at).getTime()
            : Date.now(),
          // `createChat()` creates (or returns) a backend chat, so it is persisted.
          // Marking this as `isNew` prevents `ChatTabContent` from loading turns.
          isNew: false,
          isIncognito: false,
        }

        // Replace any local placeholder chats (isNew: true) with the backend
        // chat, but preserve any chats that already have messages to avoid
        // wiping optimistic updates from in-flight sends.
        setChats((prev) => {
          // If the backend chat already exists in state, just make sure it's active
          if (prev.some((c) => c.id === backendChat.id)) {
            return prev
          }
          // Replace local-only placeholder chats; keep everything else
          const withoutPlaceholders = prev.filter(
            (c) => !c.isNew || c.messages.length > 0
          )
          return [backendChat, ...withoutPlaceholders]
        })
        setActiveChatId(result.chat.id)
      } catch (error) {
        console.error('Failed to load chats from database', error)
      }
    }

    loadChats()
  }, [
    isAuthenticated,
    authLoading,
    workbookKey,
    handleNewChat,
    setChats,
    setActiveChatId,
  ])

  // Helper: Upload files to storage (non-blocking)
  const uploadFilesToStorage = useCallback(
    async (files: FileUIPart[]): Promise<FileUIPart[]> => {
      try {
        await Promise.all(
          files.map(async (filePart) => {
            try {
              const file = await urlToFile(
                filePart.url,
                filePart.filename || 'file',
                filePart.mediaType || 'application/octet-stream'
              )
              await uploadFileToStorage(file)
            } catch (error) {
              console.warn(
                '[ChatPanel] Failed to upload file to storage:',
                error
              )
            }
          })
        )
        return files
      } catch (error) {
        console.error('[ChatPanel] Error uploading files:', error)
        return files
      }
    },
    []
  )

  // Helper: Finalize delta chain and capture after snapshot when turn completes
  const finalizeTurnSnapshotsAndDeltas = useCallback(
    async (turnId: string | null, chatId: string): Promise<void> => {
      if (!turnId || !snapshotsEnabled) return
      try {
        // Finalize delta chain + capture "after" full snapshot (hybrid mode)
        await finalizeTurnDeltas(turnId, chatId)
      } catch (error) {
        console.warn('Failed to finalize turn deltas/snapshots', error)
        // Continue anyway - non-blocking
      }
    },
    [finalizeTurnDeltas, snapshotsEnabled]
  )

  const send = useCallback(
    async (model: string, text?: string, files?: FileUIPart[]) => {
      const hasFiles = files && files.length > 0
      const hasText = text && text.trim().length > 0
      if (!hasText && !hasFiles) return

      // Clear initial prompt after first message is sent
      if (initialPrompt) {
        setInitialPrompt('')
      }

      // IMPORTANT: Capture the target chat ID at the START of send
      // This ensures messages go to the correct chat even if user switches tabs during processing
      let targetChatId = activeChatId

      // If we don't have an active chat yet for this workbook, resolve it now.
      if (!targetChatId && workbookKey) {
        try {
          const result = await createChat(workbookKey)
          if (result.success && result.chat?.id) {
            targetChatId = result.chat.id
            setChats((prev) => {
              if (prev.some((c) => c.id === result.chat.id)) return prev
              return [
                {
                  id: result.chat.id,
                  title: result.chat.title || 'New Chat',
                  messages: [],
                  createdAt: result.chat.created_at
                    ? new Date(result.chat.created_at).getTime()
                    : Date.now(),
                  // Backend chat is already persisted (even if empty).
                  isNew: false,
                  isIncognito: false,
                },
                ...prev.filter((c) => !c.isNew || c.messages.length > 0),
              ]
            })
            setActiveChatId(result.chat.id)
          }
        } catch (error) {
          console.warn(
            '[ChatPanel] Failed to resolve chat for workbookKey',
            error
          )
        }
      }

      // Get current messages from the TARGET chat (not active chat, since user may have switched tabs)
      const targetChat = targetChatId
        ? chats.find((c) => c.id === targetChatId)
        : null
      const currentMessages = targetChat?.messages ?? []
      const isIncognitoChat = Boolean(targetChat?.isIncognito)

      // Capture snapshot (non-blocking - continue even if it fails)
      const snapshotId =
        isIncognitoChat || !snapshotsEnabled
          ? null
          : await captureSnapshotForTurn()

      // Upload files to Supabase storage if present (for persistence)
      // Keep original data URLs for the model since signed URLs aren't accessible by AI models
      let uploadedFiles: FileUIPart[] = files || []
      if (!isIncognitoChat && hasFiles && files) {
        uploadedFiles = await uploadFilesToStorage(files)
      }

      // Pre-parse any attached CSV files so the pasteAttachedCsvToSheet tool
      // can write them directly without the model relaying every cell value.
      clearCsvAttachments()
      if (hasFiles && uploadedFiles.length > 0) {
        const parsed = await Promise.all(
          uploadedFiles
            .filter(
              (f) =>
                f.mediaType === 'text/csv' ||
                f.filename?.toLowerCase().endsWith('.csv')
            )
            .map(async (f) => {
              try {
                const resp = await fetch(f.url)
                const csvText = await resp.text()
                const rows = parseCsvText(csvText)
                return {
                  filename: f.filename || 'file.csv',
                  rows,
                  rowCount: rows.length,
                  columnCount: rows[0]?.length ?? 0,
                }
              } catch {
                console.warn(
                  '[ChatPanel] Failed to parse CSV attachment:',
                  f.filename
                )
                return null
              }
            })
        )
        const valid = parsed.filter(Boolean) as {
          filename: string
          rows: (string | number | null)[][]
          rowCount: number
          columnCount: number
        }[]
        if (valid.length > 0) {
          setCsvAttachments(valid)
        }
      }

      // Generate display text and build message parts
      const displayText = generateDisplayText(text, uploadedFiles)
      const parts = buildUserMessageParts(text, uploadedFiles)

      // If CSV attachments were pre-parsed, replace the CSV file parts with a
      // lightweight text hint so the model uses pasteAttachedCsvToSheet instead
      // of trying to relay every cell value through writeCellRanges.
      const csvCount = getCsvAttachmentCount()
      if (csvCount > 0) {
        // Remove CSV file parts from parts sent to the model (saves tokens)
        for (let i = parts.length - 1; i >= 0; i--) {
          const p = parts[i]
          if (
            p.type === 'file' &&
            'mediaType' in p &&
            (p.mediaType === 'text/csv' ||
              ('filename' in p &&
                typeof p.filename === 'string' &&
                p.filename.toLowerCase().endsWith('.csv')))
          ) {
            parts.splice(i, 1)
          }
        }
        // Append a hint the model will see
        parts.push({
          type: 'text',
          text: `[${csvCount} CSV file(s) attached and pre-parsed. Use the pasteAttachedCsvToSheet tool to write CSV data to the sheet. Do NOT use writeCellRanges for CSV data.]`,
        })
      }

      // Create user message
      const userMsg = createUserMessage(displayText, parts)

      // Generate chat title and create chat on first message if needed
      const chatTitle = generateChatTitle(text, files)
      targetChatId = createNewChatIfNeeded(targetChatId, chatTitle)

      // Store which chat we're sending to so we can update the correct chat later
      sendingToChatIdRef.current = targetChatId

      setLoadingChatId(targetChatId)

      // Add user message
      updateMessages((msgs) => [...msgs, userMsg], targetChatId)

      // Save user message to backend and get turnId
      let turnId: string | null = null
      if (!isIncognitoChat) {
        try {
          const userMessageResult = await saveUserMessage({
            chatId: targetChatId,
            workbookKey: workbookKey ?? undefined,
            message: userMsg,
          })
          turnId = userMessageResult.turnId

          // Mark chat as persisted now that it exists in backend
          markChatPersisted(targetChatId)

          // Store turnId in user message metadata and update the message
          if (turnId) {
            updateMessages((msgs) => {
              const updated = [...msgs]
              const lastMsg = updated[updated.length - 1]
              if (lastMsg && lastMsg.role === 'user') {
                const lastMsgWithMetadata = lastMsg as ModelMessage & {
                  metadata?: { turnId?: string }
                }
                updated[updated.length - 1] = {
                  ...lastMsg,
                  metadata: {
                    ...(lastMsgWithMetadata.metadata || {}),
                    turnId,
                  },
                } as ModelMessage
              }
              return updated
            }, targetChatId)
          }

          // Associate snapshot with turn if we captured one
          if (snapshotId && turnId && targetChatId) {
            associateSnapshot(turnId, targetChatId, snapshotId)
          }

          // Start delta capture chain for this turn
          if (turnId && targetChatId && snapshotsEnabled && !isIncognitoChat) {
            startDeltaCaptureForTurn(turnId, targetChatId)
          }

          // Update chat title if it was generated
          if (userMessageResult.title) {
            updateChatTitle(targetChatId, userMessageResult.title)
          }
        } catch (error) {
          console.error('Failed to save user message to backend', error)
          // Continue anyway - non-blocking
        }
      }

      // Create AbortController for this request
      const abortController = new AbortController()
      abortControllerRef.current = abortController

      try {
        // Stream assistant response with updated messages
        const messagesWithUser = [...currentMessages, userMsg]
        await streamChat(
          messagesWithUser,
          model,
          targetChatId,
          turnId,
          abortController.signal,
          isIncognitoChat
        )

        // Finalize delta chain + capture after snapshot (if not aborted)
        if (!isIncognitoChat && !abortController.signal.aborted && turnId) {
          await finalizeTurnSnapshotsAndDeltas(turnId, targetChatId)
        } else if (abortController.signal.aborted) {
          // Discard incomplete delta chain on abort
          discardTurnDeltas()
        }
      } catch (error) {
        // Don't show error if request was aborted
        if (abortController.signal.aborted) {
          return
        }
        console.error('Error when sending message', error)
        // Build detailed error message including cause chain for debugging
        let errorDetail = error instanceof Error ? error.message : String(error)
        if (error instanceof Error && error.cause) {
          const cause = error.cause
          errorDetail += `\n\nCause: ${cause instanceof Error ? cause.message : String(cause)}`
          if (cause instanceof Error && 'statusCode' in cause) {
            errorDetail += ` (status: ${(cause as { statusCode: number }).statusCode})`
          }
        }
        const assistantMsg: ModelMessage = {
          role: 'assistant',
          content: `Error: ${errorDetail}`,
        }
        updateMessages((msgs) => [...msgs, assistantMsg], targetChatId)
      } finally {
        setLoadingChatId(null)
        sendingToChatIdRef.current = null
        abortControllerRef.current = null
      }
    },
    [
      initialPrompt,
      activeChatId,
      workbookKey,
      chats,
      captureSnapshotForTurn,
      snapshotsEnabled,
      uploadFilesToStorage,
      createNewChatIfNeeded,
      sendingToChatIdRef,
      setLoadingChatId,
      updateMessages,
      markChatPersisted,
      associateSnapshot,
      updateChatTitle,
      setChats,
      setActiveChatId,
      abortControllerRef,
      streamChat,
      finalizeTurnSnapshotsAndDeltas,
      startDeltaCaptureForTurn,
      discardTurnDeltas,
    ]
  )

  // Show ConnectorsPage as a full-screen overlay
  if (showConnectorsPage) {
    return (
      <div className="flex flex-col h-full w-full">
        <ConnectorsPage onClose={() => setShowConnectorsPage(false)} />
      </div>
    )
  }

  // Show CreditsHistoryPage as a full-screen overlay
  if (showCreditsHistoryPage) {
    return (
      <div className="flex flex-col h-full w-full">
        <CreditsHistoryPage onClose={() => setShowCreditsHistoryPage(false)} />
      </div>
    )
  }

  // Show MemoriesPage as a full-screen overlay
  if (showMemoriesPage) {
    return (
      <div className="flex flex-col h-full w-full">
        <MemoriesPage onClose={() => setShowMemoriesPage(false)} />
      </div>
    )
  }

  return (
    <PromptInputProvider initialInput={initialPrompt}>
      <div className="relative flex flex-col h-full w-full bg-background">
        <div
          className={cn(
            'pointer-events-none absolute inset-0 bg-black/5 dark:bg-black/20 transition-opacity',
            isIncognito ? 'opacity-100' : 'opacity-0'
          )}
        />
        <Tabs
          value={effectiveActiveChatId ?? undefined}
          onValueChange={setActiveChatId}
          className="relative flex flex-col flex-1 min-h-0 w-full"
        >
          <div className="flex">
            <Settings
              onOpenConnectors={() => setShowConnectorsPage(true)}
              onOpenCreditsHistory={() => setShowCreditsHistoryPage(true)}
              onOpenMemories={() => setShowMemoriesPage(true)}
            />
            <History onSelectChat={handleSelectChatFromHistory} />
            <Button
              title="Start new chat"
              variant="ghost"
              size="icon"
              onClick={handleNewChat}
            >
              <Plus size={14} />
            </Button>
            <TabsList className="flex-1 min-w-0">
              {chats.map((conv) => (
                <TabsTrigger
                  key={conv.id}
                  value={conv.id}
                  title={conv.title}
                  className="min-w-0"
                >
                  <span className="truncate min-w-0">{conv.title}</span>
                  <div
                    onClick={(e) => {
                      e.stopPropagation()
                      handleDismiss(conv.id)
                    }}
                    className={cn(
                      buttonVariants({ variant: 'ghost', size: 'icon' }),
                      'flex-shrink-0 h-6 w-6 p-0 opacity-60 hover:opacity-100 cursor-pointer'
                    )}
                    title="Dismiss chat"
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        e.stopPropagation()
                        handleDismiss(conv.id)
                      }
                    }}
                  >
                    <X size={12} />
                  </div>
                </TabsTrigger>
              ))}
            </TabsList>
          </div>

          {chats.map((conv) => {
            const convMessages = conv?.messages ?? []
            const isActive = conv.id === effectiveActiveChatId

            return (
              <TabsContent
                key={conv.id}
                value={conv.id}
                className="flex-1 min-h-0 m-0"
              >
                <ChatTabContent
                  chatId={conv.id}
                  messages={convMessages}
                  isActive={isActive}
                  loading={loadingChatId === conv.id}
                  isNew={conv.isNew}
                  isIncognito={conv.isIncognito}
                  connectors={connectorInfos}
                  sheetSuggestions={sheetSuggestions}
                  onSuggestionClick={setInitialPrompt}
                  onMessagesLoaded={handleMessagesLoaded}
                  onTurnMetadataLoaded={handleTurnMetadataLoaded}
                />
              </TabsContent>
            )
          })}
        </Tabs>
        <div className="p-2 pt-0 flex-shrink-0">
          <ChatInput
            loading={loadingChatId !== null}
            onSend={send}
            onStop={handleStop}
            isIncognito={isIncognito}
            onToggleIncognito={handleToggleIncognito}
          />
        </div>
      </div>
    </PromptInputProvider>
  )
}
